import * as React from "react";
import { useNavigate } from "react-router";
import Api from "@/api";

import * as Time from "@/utils/time";
import * as People from "@/models/people";
import * as Milestones from "@/models/milestones";
import * as Tasks from "@/models/tasks";
import * as Projects from "@/models/projects";
import * as Activities from "@/models/activities";
import { parseActivitiesForTurboUi, MILESTONE_ACTIVITY_TYPES } from "@/models/activities/feed";

import { showErrorToast, MilestonePage, Timeline } from "turboui";
import { Paths, usePaths } from "@/routes/paths";
import { PageCache } from "@/routes/PageCache";
import { fetchAll } from "@/utils/async";
import { useMe } from "@/contexts/CurrentCompanyContext";
import { assertPresent } from "@/utils/assertions";
import { parseSpaceForTurboUI, useSpaceSearch as useTaskDestinationSpaceSearch } from "@/models/spaces";
import { PageModule } from "@/routes/types";
import { parseContextualDate, serializeContextualDate } from "@/models/contextualDates";
import { projectPageCacheKey } from "../ProjectPage";
import { milestoneKanbanPageCacheKey } from "../MilestoneKanbanPage";
import { spaceKanbanPageCacheKey } from "../SpaceKanbanPage";
import { useComments } from "./useComments";
import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";
import { useSubscription } from "@/models/subscriptions";
import { useMilestones as useProjectMilestones } from "@/models/milestones/useMilestones";

export default { name: "MilestonePage", loader, Page } as PageModule;

type TurboUiComment = Timeline.Comment | Timeline.MilestoneActivity;

type LoaderResult = {
  data: {
    milestone: Milestones.Milestone;
    tasks: Tasks.Task[];
    childrenCount: Projects.ProjectChildrenCount;
    activities: Activities.Activity[];
  };
  cacheVersion: number;
};

async function loader({ params, refreshCache = false }): Promise<LoaderResult> {
  return await PageCache.fetch({
    cacheKey: pageCacheKey(params.id),
    refreshCache,
    fetchFn: () =>
      fetchAll({
        milestone: Milestones.getMilestone({
          id: params.id,
          includeProject: true,
          includeCreator: true,
          includeSpace: true,
          includePermissions: true,
          includeComments: true,
          includeSubscriptionList: true,
          includeAvailableStatuses: true,
        }).then((d) => d.milestone),
        tasks: Api.projects.listMilestoneTasks({ milestoneId: params.id }).then((d) => d.tasks),
        childrenCount: Api.projects.countChildren({ id: params.id, useMilestoneId: true }).then((d) => d.childrenCount),
        activities: Api.companies
          .listActivities({
            scopeId: params.id,
            scopeType: "milestone",
            actions: MILESTONE_ACTIVITY_TYPES,
          })
          .then((d) => d.activities!),
      }),
  });
}

function pageCacheKey(id: string): string {
  return `v12-MilestoneV2Page.task-${id}`;
}

function Page() {
  const paths = usePaths();
  const currentUser = useMe();
  const navigate = useNavigate();

  const pageData = PageCache.useData(loader);
  const { data, refresh } = pageData;
  const { milestone, childrenCount, activities, tasks: backendTasks } = data;

  assertPresent(milestone.project, "Milestone must have a project");
  assertPresent(milestone.permissions, "Milestone must have permissions");
  const projectId = milestone.project.id;

  const spaceProps = milestone.space
    ? {
        workmapLink: paths.spaceWorkMapPath(milestone.space.id, "projects" as const),
        space: parseSpaceForTurboUI(paths, milestone.space),
      }
    : { homeLink: paths.homePath() };

  const [projectName, setProjectName] = usePageField(pageData, {
    value: ({ milestone }) => milestone.project?.name!,
    update: (v) => Api.projects.updateName({ projectId: milestone.project?.id || "", name: v }),
    onError: (e: string) => showErrorToast(e, "Reverted the project name to its previous value."),
    validations: [(v) => (v?.trim() === "" ? "Project name cannot be empty" : null)],
  });

  const [description, setDescription] = usePageField(pageData, {
    value: ({ milestone }) => milestone.description && JSON.parse(milestone.description),
    update: (v) =>
      Api.projects.updateMilestoneDescription({ milestoneId: milestone.id, description: JSON.stringify(v) }),
    onError: () => showErrorToast("Error", "Failed to update milestone description."),
  });

  const [dueDate, setDueDate] = usePageField(pageData, {
    value: ({ milestone }) => parseContextualDate(milestone.timeframe?.contextualEndDate),
    update: (v) =>
      Api.projects.updateMilestoneDueDate({ milestoneId: milestone.id, dueDate: serializeContextualDate(v) }),
    onError: (e: string) => showErrorToast(e, "Failed to update milestone due date."),
  });

  const { parsedMilestone, milestones, setMilestones, title, setTitle } = useMilestones(pageData, milestone);

  const {
    tasks,
    createTask,
    updateTaskAssignee,
    updateTaskDueDate,
    updateTaskReminders,
    updateTaskStatus,
    updateTaskMilestone,
    updateTaskName,
    updateTaskDescription,
    deleteTask,
  } = Tasks.useProjectTasksForTurboUi({
    backendTasks,
    projectId: milestone.project.id,
    cacheKey: pageCacheKey(milestone.id),
    milestones: milestones,
    setMilestones: setMilestones,
    refresh,
  });
  const {
    comments,
    setComments,
    handleCreateComment,
    handleEditComment,
    handleDeleteComment,
    handleAddReaction,
    handleRemoveReaction,
  } = useComments(paths, milestone, () => {
    PageCache.invalidate(pageCacheKey(milestone.id));
  });
  const [status, setStatus] = useStatusField(paths, pageData, setComments);

  const timelineItems = React.useMemo(
    () => prepareTimelineItems(paths, activities, comments),
    [paths, activities, comments],
  );

  const handleDelete = React.useCallback(async () => {
    await Api.projects.deleteMilestone({ milestoneId: milestone.id });

    if (milestone.project) {
      PageCache.invalidate(projectPageCacheKey(milestone.project.id));
      navigate(paths.projectPath(milestone.project.id, { tab: "tasks" }));
    } else {
      navigate(paths.homePath());
    }
  }, [milestone.id]);

  const richEditorHandlers = useRichEditorHandlers({ scope: { type: "project", id: milestone.project.id } });
  const formattedTimePreferences = useFormattedTimePreferences();
  const { milestones: searchableMilestones, search: searchMilestones } = useProjectMilestones(milestone.project.id);
  const taskProjectSearch = Projects.useProjectSearch({
    accessLevel: "edit_access",
    ignoredIds: [milestone.project.id],
    activeOnly: true,
  });
  const taskSpaceSearch = useTaskDestinationSpaceSearch({ accessLevel: "edit_access", withTasksEnabledOnly: true });

  // Transform function must be memoized to prevent infinite loop in the hook
  const transformPerson = React.useCallback((p) => People.parsePersonForTurboUi(paths, p)!, [paths]);

  const assigneeSearch = Tasks.useTaskAssigneeSearch({
    id: milestone.project.id,
    type: "project",
    transformResult: transformPerson,
  });

  const subscriptions = useSubscription({
    subscriptionList: milestone.subscriptionList,
    entityId: milestone.id,
    entityType: "milestone",
    cacheKey: pageCacheKey(milestone.id),
    onRefresh: refresh,
  });

  const handleMoveTaskSuccess = React.useCallback(
    async ({ destinationType, destinationId }: { destinationType: string; destinationId: string }) => {
      PageCache.invalidate(pageCacheKey(milestone.id));
      PageCache.invalidate(milestoneKanbanPageCacheKey(milestone.id));

      if (milestone.project?.id) {
        PageCache.invalidate(projectPageCacheKey(milestone.project.id));
      }

      if (destinationType === "project") {
        PageCache.invalidate(projectPageCacheKey(destinationId));
      }

      if (destinationType === "space") {
        PageCache.invalidate(spaceKanbanPageCacheKey(destinationId));
      }

      if (refresh) {
        await refresh();
      }
    },
    [milestone.id, milestone.project?.id, refresh],
  );

  const handleTaskMilestoneChange = React.useCallback(
    async (taskId: string, nextMilestone: MilestonePage.Milestone | null) => {
      const result = await updateTaskMilestone(taskId, nextMilestone?.id ?? null, 1000);

      if (!result) return;

      PageCache.invalidate(projectPageCacheKey(projectId));
      PageCache.invalidate(milestoneKanbanPageCacheKey(milestone.id));

      if (nextMilestone?.id) {
        PageCache.invalidate(pageCacheKey(nextMilestone.id));
        PageCache.invalidate(milestoneKanbanPageCacheKey(nextMilestone.id));
      }
    },
    [milestone.id, projectId, updateTaskMilestone],
  );

  const handleTaskDelete = React.useCallback(
    async (taskId: string) => {
      const result = await deleteTask(taskId);

      if (!result?.success) return;

      PageCache.invalidate(pageCacheKey(milestone.id));
      PageCache.invalidate(milestoneKanbanPageCacheKey(milestone.id));
      PageCache.invalidate(projectPageCacheKey(projectId));
    },
    [deleteTask, milestone.id, projectId],
  );

  const slideInModel = Tasks.useTaskSlideInProps({
    backendTasks,
    paths,
    currentUser,
    tasks,
    commentEntityType: "project_task",
    cacheKey: pageCacheKey(milestone.id),
    onRefresh: refresh,
    canEdit: Boolean(milestone.permissions.canEdit),
    canComment: Boolean(milestone.permissions.canComment),
    onTaskNameChange: updateTaskName,
    onTaskAssigneeChange: updateTaskAssignee,
    onTaskDueDateChange: updateTaskDueDate,
    onTaskRemindersChange: updateTaskReminders,
    onTaskStatusChange: updateTaskStatus,
    onTaskDescriptionChange: updateTaskDescription,
    onMoveTaskSuccess: handleMoveTaskSuccess,
    projectSearch: taskProjectSearch,
    spaceSearch: taskSpaceSearch,
  });

  const statusOptions = React.useMemo(
    () => Tasks.parseTaskStatusesForTurboUi(milestone.availableStatuses),
    [milestone.availableStatuses],
  );

  const props: MilestonePage.Props = {
    ...spaceProps,
    childrenCount,
    permissions: milestone.permissions,

    assigneePersonSearch: assigneeSearch,

    // Project
    projectName,
    projectLink: paths.projectPath(milestone.project.id),
    projectStatus: milestone.project.status,
    updateProjectName: setProjectName,

    // Milestone data
    milestone: parsedMilestone,

    // Timeline/Comments
    currentUser: People.parsePersonForTurboUi(paths, currentUser)!,
    timelineItems,
    onAddComment: handleCreateComment,
    onEditComment: handleEditComment,
    onDeleteComment: handleDeleteComment,
    onAddReaction: handleAddReaction,
    onRemoveReaction: handleRemoveReaction,
    localDraftKeyBase: `milestone:${milestone.id}`,

    // Core milestone data
    title,
    onMilestoneTitleChange: setTitle,
    description,
    onDescriptionChange: setDescription,
    dueDate,
    onDueDateChange: setDueDate,
    status,
    onStatusChange: setStatus,

    onDelete: handleDelete,

    // Tasks
    tasks,
    statusOptions,
    onTaskCreate: createTask,
    onTaskReorder: updateTaskMilestone,
    onTaskMilestoneChange: handleTaskMilestoneChange,
    onTaskStatusChange: slideInModel.onTaskStatusChange,
    onTaskAssigneeChange: slideInModel.onTaskAssigneeChange,
    onTaskDueDateChange: slideInModel.onTaskDueDateChange,
    onTaskRemindersChange: slideInModel.onTaskRemindersChange,
    onTaskNameChange: slideInModel.onTaskNameChange,
    onTaskDescriptionChange: slideInModel.onTaskDescriptionChange,
    onTaskDelete: handleTaskDelete,
    milestones: searchableMilestones,
    onMilestoneSearch: searchMilestones,
    getTaskPageProps: slideInModel.getTaskPageProps,

    // Metadata
    createdBy: People.parsePersonForTurboUi(paths, milestone.creator),
    createdAt: Time.parseDate(milestone.insertedAt)!,

    // Subscriptions
    subscriptions,

    // Rich text editor support
    richTextHandlers: richEditorHandlers,
    formattedTimePreferences,
  };

  return <MilestonePage key={milestone.id!} {...props} />;
}

function usePageField<T>(
  pageData: LoaderResult & { refresh?: () => Promise<void> },
  {
    value,
    update,
    onError,
    validations,
  }: {
    value: (data: { milestone: Milestones.Milestone }) => T;
    update: (value: T) => Promise<any>;
    onError?: (error: string) => void;
    validations?: ((value: T) => string | null)[];
  },
): [T, (v: T) => Promise<boolean>] {
  const { cacheVersion, data, refresh: refreshPageData } = pageData;

  const [state, setState] = React.useState<T>(() => value(data));
  const [stateVersion, setStateVersion] = React.useState<number | undefined>(cacheVersion);

  React.useEffect(() => {
    if (cacheVersion !== stateVersion) {
      setState(value(data));
      setStateVersion(cacheVersion);
    }
  }, [value, cacheVersion, stateVersion, data]);

  const updateState = async (newVal: T): Promise<boolean> => {
    if (validations) {
      for (const validation of validations) {
        const error = validation(newVal);
        if (error) {
          onError?.(error);
          return false;
        }
      }
    }

    try {
      setState(newVal);
      await update(newVal);

      // Invalidate the cache for this entity
      if (data.milestone.id) {
        PageCache.invalidate(pageCacheKey(data.milestone.id));
      }
      if (data.milestone.project?.id) {
        PageCache.invalidate(projectPageCacheKey(data.milestone.project.id));
      }

      // Refresh the page data if requested
      if (refreshPageData) {
        await refreshPageData();
      }

      return true;
    } catch (e) {
      setState(value(data));
      onError?.(e instanceof Error ? e.message : String(e));
      return false;
    }
  };

  return [state, updateState];
}

function prepareTimelineItems(paths: Paths, activities: Activities.Activity[], comments: TurboUiComment[]) {
  const parsedActivities: MilestonePage.TimelineItemType[] = parseActivitiesForTurboUi(paths, activities, "milestone")
    .filter((activity): activity is NonNullable<typeof activity> => activity !== null)
    .map((activity) => {
      if (activity.type.startsWith("task_")) {
        return { type: "task-activity", value: activity as any };
      } else {
        return { type: "milestone-activity", value: activity as any };
      }
    });

  const timelineItems = comments.map((comment) => {
    const type = "type" in comment ? "milestone-activity" : "comment";

    return { type, value: comment } as MilestonePage.TimelineItemType;
  });

  return [...parsedActivities, ...timelineItems].sort((a, b) => {
    // Special handling for temporary comments - always show them last
    const aIsTemp = a.value.id.startsWith("temp-");
    const bIsTemp = b.value.id.startsWith("temp-");

    // If one is temporary and the other isn't, prioritize the non-temporary one
    if (aIsTemp && !bIsTemp) return 1;
    if (!aIsTemp && bIsTemp) return -1;

    const aInsertedAt = a.type === "acknowledgment" ? a.insertedAt : a.value.insertedAt;
    const bInsertedAt = b.type === "acknowledgment" ? b.insertedAt : b.value.insertedAt;

    return aInsertedAt.localeCompare(bInsertedAt);
  });
}

function useStatusField(
  paths: Paths,
  pageData: LoaderResult,
  setComments: React.Dispatch<React.SetStateAction<TurboUiComment[]>>,
) {
  const me = useMe()!;

  const { data } = pageData;
  const milestone = data.milestone;

  const [status, setStatus] = usePageField(pageData, {
    value: ({ milestone }) => milestone.status,
    update: async (v) => {
      const tmpId = `temp-${Date.now()}`;
      const optimisticComment: Milestones.MilestoneComment = {
        __typename: "milestone_comment",
        action: v === "done" ? "complete" : "reopen",
        comment: {
          __typename: "comment",
          id: tmpId,
          insertedAt: new Date().toISOString(),
          author: me,
        },
      };

      setComments((prev) => [...prev, Milestones.parseMilestoneCommentForTurboUi(paths, optimisticComment)]);

      const res = await Api.projects.createMilestoneComment({
        milestoneId: milestone.id,
        content: null,
        action: v === "done" ? "complete" : "reopen",
      });

      PageCache.invalidate(pageCacheKey(milestone.id));

      setComments((prev) =>
        prev.map((c) => {
          if (c.id === tmpId) {
            const comment = { ...res.comment.comment, author: me };
            return Milestones.parseMilestoneCommentForTurboUi(paths, { ...res.comment, comment });
          } else {
            return c;
          }
        }),
      );
    },
    onError: (e: string) => showErrorToast(e, "Failed to update milestone status."),
  });

  return [status, setStatus] as const;
}

function useMilestones(pageData, milestone: Milestones.Milestone) {
  const paths = usePaths();
  const [milestones, setMilestones] = React.useState<MilestonePage.Milestone[]>(
    Milestones.parseMilestonesForTurboUi(paths, [milestone]).orderedMilestones,
  );

  const parsedMilestone = milestones[0]!;

  const [title, setTitle] = usePageField(pageData, {
    value: ({ milestone }) => milestone.title,
    update: (v) => Api.projects.updateMilestoneTitle({ milestoneId: milestone.id, title: v }),
    onError: (e: string) => showErrorToast(e, "Failed to update milestone name."),
    validations: [(v) => (v.trim() === "" ? "Milestone name cannot be empty" : null)],
  });

  return {
    title,
    setTitle,
    parsedMilestone,
    milestones,
    setMilestones,
  };
}
